'use client';

import {
	useQuery,
	useMutation,
	useQueryClient,
	QueryClient,
} from '@tanstack/react-query';
import useApiAuth, { type ApiAuthHeaders } from 'src/hooks/useApiAuth';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { notify } from 'src/utils/notifications';
import { createQueryKey } from 'src/utils/query/createQueryKey';
import Env from 'src/environmentVariables/EnvironmentVariables';

// ── Types ──

export interface WhitelistRecord {
	whitelistId: string;
	authorityId: string;
	address: string;
	label: string;
	token: string;
	chainId: string;
}

export type { ApiAuthHeaders };

// ── Fetch helper ──

const WHITELIST_BASE = '/whitelist';

async function whitelistFetch<T>(
	path: string,
	authHeaders: ApiAuthHeaders,
	options?: { method?: string; body?: string }
): Promise<T> {
	const url = `${Env.dataApiUrl}${path}`;
	const headers: Record<string, string> = { ...authHeaders };
	if (options?.body) {
		headers['Content-Type'] = 'application/json';
	}
	const response = await fetch(url, {
		...options,
		headers,
	});

	if (!response.ok) {
		const errorText = await response.text();
		throw new Error(`Whitelist API error: ${response.status} - ${errorText}`);
	}

	return response.json();
}

// ── Query key ──

function getQueryKey(authority: string | undefined) {
	return createQueryKey('whitelist', authority);
}

function invalidateWhitelist(
	queryClient: QueryClient,
	authority: string | undefined
) {
	queryClient.invalidateQueries({ queryKey: getQueryKey(authority) });
}

// ── Hooks ──

export function useWhitelistAddresses(enabled = true) {
	const authority = useVelocityStore(
		(s) => s.wallet.current.adapter?.publicKey
	);
	const authorityStr = authority?.toBase58();
	const { getAuthHeaders, isConnected } = useApiAuth();

	return useQuery({
		queryKey: getQueryKey(authorityStr),
		queryFn: async () => {
			const headers = await getAuthHeaders();
			const response = await whitelistFetch<{
				success: boolean;
				items: WhitelistRecord[];
			}>(WHITELIST_BASE, headers);
			return response.items;
		},
		enabled: isConnected && enabled,
		staleTime: 30_000,
	});
}

export function useCreateWhitelistAddress() {
	const queryClient = useQueryClient();
	const authority = useVelocityStore(
		(s) => s.wallet.current.adapter?.publicKey
	);
	const { getAuthHeaders } = useApiAuth();

	return useMutation({
		mutationFn: async (params: {
			address: string;
			label: string;
			token: string;
			chainId: string;
		}) => {
			const headers = await getAuthHeaders();
			return whitelistFetch<{ success: boolean; item: WhitelistRecord }>(
				WHITELIST_BASE,
				headers,
				{
					method: 'POST',
					body: JSON.stringify(params),
				}
			);
		},
		onSuccess: () => {
			invalidateWhitelist(queryClient, authority?.toBase58());
			notify({
				type: 'success',
				message: 'Address added to address book',
			});
		},
		onError: (error: Error) => {
			notify({
				type: 'error',
				message: error.message || 'Failed to add address',
			});
		},
	});
}

export function useUpdateWhitelistAddress() {
	const queryClient = useQueryClient();
	const authority = useVelocityStore(
		(s) => s.wallet.current.adapter?.publicKey
	);
	const { getAuthHeaders } = useApiAuth();

	return useMutation({
		mutationFn: async (params: {
			whitelistId: string;
			address: string;
			label: string;
			token: string;
			chainId: string;
		}) => {
			const headers = await getAuthHeaders();
			return whitelistFetch<{ success: boolean; item: WhitelistRecord }>(
				`${WHITELIST_BASE}/${params.whitelistId}`,
				headers,
				{
					method: 'PUT',
					body: JSON.stringify({
						address: params.address,
						label: params.label,
						token: params.token,
						chainId: params.chainId,
					}),
				}
			);
		},
		onSuccess: () => {
			invalidateWhitelist(queryClient, authority?.toBase58());
			notify({
				type: 'success',
				message: 'Address updated',
			});
		},
		onError: (error: Error) => {
			notify({
				type: 'error',
				message: error.message || 'Failed to update address',
			});
		},
	});
}

export function useDeleteWhitelistAddress() {
	const queryClient = useQueryClient();
	const authority = useVelocityStore(
		(s) => s.wallet.current.adapter?.publicKey
	);
	const { getAuthHeaders } = useApiAuth();

	return useMutation({
		mutationFn: async (whitelistId: string) => {
			const headers = await getAuthHeaders();
			return whitelistFetch<{ success: boolean }>(
				`${WHITELIST_BASE}/${whitelistId}`,
				headers,
				{ method: 'DELETE' }
			);
		},
		onSuccess: () => {
			invalidateWhitelist(queryClient, authority?.toBase58());
			notify({
				type: 'success',
				message: 'Address removed from address book',
			});
		},
		onError: (error: Error) => {
			notify({
				type: 'error',
				message: error.message || 'Failed to delete address',
			});
		},
	});
}
